import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Task } from '@repo/shared';
import { useParams } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';

import { useDatasetById } from '@/api/generated';
import type {
  DescribeImageInstance,
  ExtractStructureInstance,
  GenerateAudioModelRun,
  TextTranslationInstance,
} from '@/api/generated.schemas';
import { DataTable } from '@/components/blocks/data-table/data-table';
import Header from '@/components/header';
import { Spinner } from '@/components/ui/spinner';
import { columns as audioGenerationColumns } from '@/routes/(app)/(datasets)/components/audio-generation-instance-columns';
import { columns as describeImageColumns } from '@/routes/(app)/(datasets)/components/describe-image-instance-columns';
import { columns as extractStructureColumns } from '@/routes/(app)/(datasets)/components/extract-structure-instance-columns';
import { columns as textTranslationColumns } from '@/routes/(app)/(datasets)/components/text-translation-instance-columns';

type Instance =
  | TextTranslationInstance
  | GenerateAudioModelRun
  | ExtractStructureInstance
  | DescribeImageInstance;

const DatasetDetails = () => {
  const { t } = useTranslation();
  const { datasetId } = useParams({ from: '/app/datasets/$datasetId' });
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data, isLoading } = useDatasetById(datasetId);

  const columns: Array<ColumnDef<Instance>> = useMemo(() => {
    if (data?.task === Task.TEXT_TRANSLATION) {
      return textTranslationColumns as Array<ColumnDef<Instance>>;
    }
    if (data?.task === Task.GENERATE_AUDIO) {
      return audioGenerationColumns as Array<ColumnDef<Instance>>;
    }
    if (data?.task === Task.EXTRACT_STRUCTURE) {
      return extractStructureColumns as Array<ColumnDef<Instance>>;
    }
    if (data?.task === Task.DESCRIBE_IMAGE) {
      return describeImageColumns as Array<ColumnDef<Instance>>;
    }

    return [];
  }, [data?.task]);

  const table = useReactTable<Instance>({
    state: {
      pagination,
      sorting,
    },
    data: data?.instances as Array<Instance>,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
  });

  if (isLoading) return <Spinner />;

  if (!data) return null;

  return (
    <>
      <Header
        title={data.name}
        description={t('datasets.details.description', {
          count: data.instances.length,
        })}
      />
      {[
        Task.TEXT_TRANSLATION,
        Task.GENERATE_AUDIO,
        Task.EXTRACT_STRUCTURE,
        Task.DESCRIBE_IMAGE,
      ].includes(data.task as Task) ? (
        <DataTable table={table} />
      ) : (
        <span>Not supported yet!</span>
      )}
    </>
  );
};

export default DatasetDetails;
